import EventEmitter from 'events'
import Resumable from 'resumablejs'
import { v4 as uuid } from 'uuid'
import { Config } from '@/services/ConfigService'
import { getFileExtension } from '@/helpers/files'
import Interceptor from '@/plugins/axios/DefaultInterceptor'

import { Bus as AppEventBus, Events as AppEvents } from '@/events/AppEvents'

const MAX_CONCURRENT_UPLOADS = 4
const MAX_RETRIES = 1

class Job {
	constructor(url, file, uploadObject, params = {}) {
		this.url = url
		this.file = file
		this.uploadObject = uploadObject
		this.parameters = { ...params }
		this.parameters.name = this.filename
		this.tries = 0
		this.resumable = new Resumable({
			chunkRetryInterval: Config.VUE_APP_UPLOAD_RETRY_INTERVAL,
			chunkSize: Config.VUE_APP_UPLOAD_CHUNK_SIZE,
			headers: {
				Accept: 'application/json',
				Authorization: localStorage.getItem('token')
			},
			forceChunkSize: true,
			prioritizeFirstAndLastChunk: false,
			permanentErrors: [400, 401, 403, 404, 415, 422, 500, 501],
			query: { ...this.parameters },
			simultaneousUploads: Config.VUE_APP_UPLOAD_MAX_CONCURRENT_CHUNKS,
			target: this.url,
			testChunks: false
		})
	}

	get filename() {
		let result = this.file.name
		if (this.file.name && this.file.name.length >= 191) {
			const splittedName = this.file.name.split('.')
			const extension = splittedName.pop()
			const name = splittedName.join('.').substring(0, 190 - (extension.length + 1))
			result = name + '.' + extension
		}
		return result
	}

	get uploadId() {
		return this.uploadObject.id
	}

	setupFileOriginalName() {
		Object.defineProperty(this.file, 'original_name', {
			value: this.file.name,
			writable: true,
			enumerable: true
		})
	}
	setupFileName() {
		Object.defineProperty(this.file, 'name', {
			value: `${Date.now().toString()}-${(crypto.getRandomValues(new Uint32Array(1))[0] % 100).toString()}.${getFileExtension(this.file?.name)}`,
			writable: true,
			enumerable: true
		})
	}
	setupResumable(resolve) {
		this.resumable.on('fileAdded', () => {
			this.resumable.upload()
		})

		this.resumable.on('progress', () => {
			const progress = this.resumable.progress()
			this.uploadObject.progression = Math.round(progress * 100)
			AppEventBus.emit(AppEvents.UPDATE_UPLOAD_PROGRESS, this.uploadObject)
		})

		this.resumable.on('error', (error, failedFile) => {
			if (this.tries < MAX_RETRIES) {
				this.tries++
				failedFile.retry()
			} else {
				let jsonError = {}
				try {
					jsonError = JSON.parse(error)
				} catch (e) {
					jsonError = { message: window.vueInstance.$t('documents.upload.unknown_error') }
				}

				AppEventBus.emit(AppEvents.UPLOAD_FAILED, { uploadId: this.uploadObject.id, error: Interceptor.getErrorMessage(jsonError) })
				AppEventBus.emit(AppEvents.REMOVE_UPLOAD_PROGRESS, this.uploadObject.id)
				resolve(null)
			}
		})

		this.resumable.on('fileSuccess', successedFile => {
			let data = successedFile?.chunks?.map(chunk => JSON.parse(chunk.message())).find(chunkData => chunkData?.data?.id && !chunkData?.data?.done) || {}

			AppEventBus.emit(AppEvents.COMPLETE_UPLOAD_PROGRESS, this.uploadObject.id)
			resolve(data)
		})
	}

	cancel() {
		if (Config.VUE_APP_UPLOAD_ENABLE_CANCEL) {
			this.resumable.cancel()
			AppEventBus.emit(AppEvents.REMOVE_UPLOAD_PROGRESS, this.uploadObject.id)
		}
	}

	pause() {
		this.resumable.pause()
	}

	resume() {
		this.resumable.upload()
	}

	run() {
		let result = Promise.resolve()
		if (this.file.size > 0) {
			this.setupFileOriginalName()
			this.setupFileName()
			result = new Promise(this.setupResumable.bind(this))
			this.resumable.addFile(this.file)
		} else {
			AppEventBus.emit(AppEvents.SNACKBAR_WARNING, window.vueInstance.$t('documents.errors.upload_is_empty'))
			AppEventBus.emit(AppEvents.REMOVE_UPLOAD_PROGRESS, this.uploadObject.id)
		}
		return result
	}
}

class UploadManager extends EventEmitter {
	constructor() {
		super()
		this.runningJobs = 0
		this.queue = []
		this.cancelHandlers = new Map()
		this.cancelledUploads = new Set()
		this.activeJobs = new Map()
		this.isOffline = typeof navigator !== 'undefined' && !navigator.onLine
		this._setupConnectivityListeners()
	}

	_setupConnectivityListeners() {
		if (typeof window !== 'undefined') {
			window.addEventListener('offline', this.pauseAll.bind(this))
			window.addEventListener('online', this.resumeAll.bind(this))
		}
	}

	pauseAll() {
		if (!this.isOffline) {
			this.isOffline = true
			this.activeJobs.forEach(job => job.pause())
		}
	}

	resumeAll() {
		if (this.isOffline) {
			this.isOffline = false
			this.activeJobs.forEach(job => job.resume())
			this._fillAvailableSlots()
		}
	}

	_fillAvailableSlots() {
		while (!this.isOffline && this.runningJobs < MAX_CONCURRENT_UPLOADS && this.queue.length > 0) {
			const job = this.queue.shift()
			if (this.cancelledUploads.has(job.uploadId)) {
				this._cancelJob(job)
				continue
			}
			this.runningJobs++
			this.activeJobs.set(job.uploadId, job)
			this.runJob(job)
		}
	}

	cleanup() {
		this.runningJobs = 0
		this.queue = []
		this.activeJobs.clear()
		this.cancelHandlers.forEach(handler => {
			AppEventBus.bus.$off(AppEvents.CANCEL_UPLOAD, handler)
		})
		this.cancelHandlers.clear()
		this.cancelledUploads.clear()
	}

	_cancelJob(job) {
		if (Config.VUE_APP_UPLOAD_ENABLE_CANCEL) {
			this.cancelledUploads.add(job.uploadId)
			job.cancel()
			this._cleanupHandler(job.uploadId)
		}
	}

	_cancelAllUploads() {
		// Cancel all queued and running jobs
		const allJobs = [...this.queue, ...this.activeJobs.values()]
		allJobs.forEach(job => this._cancelJob(job))

		this.queue = []
		this.activeJobs.clear()
		this.runningJobs = 0
		AppEventBus.emit(AppEvents.CANCEL_UPLOAD, 'all')
	}

	_cancelActiveJob(uploadId, activeJob) {
		this._cancelJob(activeJob)
		this.activeJobs.delete(uploadId)
		this.runningJobs = Math.max(0, this.runningJobs - 1)

		// Start next job from queue if available (but not while offline — resumeAll() will handle it)
		const nextJob = this.isOffline ? null : this.queue.shift()
		if (nextJob && !this.cancelledUploads.has(nextJob.uploadId)) {
			this.runningJobs++
			this.activeJobs.set(nextJob.uploadId, nextJob)
			this.runJob(nextJob)
		}
	}

	_cancelQueuedJob(uploadId) {
		const queueIndex = this.queue.findIndex(job => job.uploadId === uploadId)
		if (queueIndex !== -1) {
			const [cancelledJob] = this.queue.splice(queueIndex, 1)
			this._cancelJob(cancelledJob)
		}
	}

	cancelUpload(uploadId) {
		if (Config.VUE_APP_UPLOAD_ENABLE_CANCEL) {
			if (uploadId === 'all') {
				this._cancelAllUploads()
			} else {
				// Add to cancelled uploads set
				this.cancelledUploads.add(uploadId)

				// First check running jobs, then queued jobs
				const activeJob = this.activeJobs.get(uploadId)
				if (activeJob) {
					this._cancelActiveJob(uploadId, activeJob)
				} else {
					this._cancelQueuedJob(uploadId)
				}
			}
		}
	}

	_cleanupHandler(uploadId) {
		const handler = this.cancelHandlers.get(uploadId)
		if (handler) {
			AppEventBus.bus.$off(AppEvents.CANCEL_UPLOAD, handler)
			this.cancelHandlers.delete(uploadId)
		}
	}

	addJob(job) {
		if (this.cancelledUploads.has(job.uploadId)) {
			// Don't add job if it's been cancelled
			AppEventBus.emit(AppEvents.REMOVE_UPLOAD_PROGRESS, job.uploadId)
		} else if (!this.isOffline && this.runningJobs < MAX_CONCURRENT_UPLOADS) {
			// While offline, every new upload just waits in the queue until resumeAll() drains it.
			this.runningJobs++
			this.activeJobs.set(job.uploadId, job)
			this.runJob(job)
		} else {
			this.queue.push(job)
		}
	}

	onJobFinished(jobId) {
		this.runningJobs = Math.max(0, this.runningJobs - 1)
		this.activeJobs.delete(jobId)
		this.cancelledUploads.delete(jobId)

		const jobToRun = this._dequeueNextRunnableJob()
		if (jobToRun) {
			this.runningJobs++
			this.activeJobs.set(jobToRun.uploadId, jobToRun)
			this.runJob(jobToRun)
		} else if (this.runningJobs === 0 && this.queue.length === 0) {
			// Note: queue.length is always 0 here unless we're offline (see jobToRun above) — in
			// that case uploading isn't really "ended", it's just paused until resumeAll() runs.
			AppEventBus.emit(AppEvents.UPLOAD_ENDED)
		}
	}

	// Don't start the next queued job while offline — it'll be picked up by resumeAll(). Otherwise,
	// pop jobs off the queue, discarding any that were cancelled while they sat there, until one is
	// found to run or the queue is empty.
	_dequeueNextRunnableJob() {
		let result = null

		if (!this.isOffline) {
			while (this.queue.length) {
				const job = this.queue.shift()
				if (!this.cancelledUploads.has(job.uploadId)) {
					result = job
					break
				}

				this._cancelJob(job)
				this.runningJobs = Math.max(0, this.runningJobs - 1)
				this.activeJobs.delete(job.uploadId)
				this.cancelledUploads.delete(job.uploadId)
			}
		}

		return result
	}

	runJob(job) {
		return job
			.run()
			.then(res => {
				this.emit(`${job.uploadId}_UPLOADED`, res)
			})
			.finally(() => {
				this.onJobFinished(job.uploadId)
			})
	}

	_createUploadJob(url, file, params, fileId) {
		const uploadObject = {
			id: fileId,
			name: file.name,
			size: file.size,
			type: file.type,
			path: file.path || file.webkitRelativePath || file.name,
			progression: 0
		}

		AppEventBus.emit(AppEvents.ADD_UPLOAD_PROGRESS, uploadObject)

		const job = new Job(url, file, uploadObject, params)

		const cancelHandler = cancelledUploadId => {
			if (cancelledUploadId === fileId || cancelledUploadId === 'all') {
				this.cancelledUploads.add(fileId)
				job.cancel()
				this._cleanupHandler(fileId)
			}
		}

		this.cancelHandlers.set(fileId, cancelHandler)
		AppEventBus.bus.$on(AppEvents.CANCEL_UPLOAD, cancelHandler)

		return job
	}

	upload(url, file, params = {}) {
		const fileId = file.id ?? uuid()
		let result = Promise.resolve()

		// Check if upload was cancelled
		if (!this.cancelledUploads.has(fileId)) {
			const job = this._createUploadJob(url, file, params, fileId)

			this.addJob(job)
			result = new Promise(resolve => {
				this.once(`${fileId}_UPLOADED`, data => {
					this._cleanupHandler(fileId)
					this.cancelledUploads.delete(fileId)
					resolve(data)
				})
			})
		}

		return result
	}
}

const UploadManagerSingleton = new UploadManager()

export default UploadManagerSingleton
